import { useEffect, useState } from 'react';
import { useTheme } from '@/context/ThemeContext';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navBg = scrolled
    ? isDark
      ? 'bg-[#0D1B2A]/80 backdrop-blur-xl shadow-2xl border-b border-white/5'
      : 'bg-white/80 backdrop-blur-xl shadow-lg border-b border-gray-100'
    : 'bg-transparent';

  const sectionBg = isDark ? '#0D1B2A' : '#F5F8FA';
  const nodeRectFill = isDark ? '#0D1B2A' : '#E8F0F7';

  return (
    <section
      className="relative min-h-screen flex flex-col overflow-hidden transition-colors duration-500"
      style={{ background: sectionBg }}
    >
      {/* Subtle Grid Background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(20,184,166,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(20,184,166,0.04) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Radial vignette over grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at center, transparent 30%, ${sectionBg} 80%)`,
        }}
      />

      {/* Frosted Glass Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg}`}>
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://public.readdy.ai/ai/img_res/b0ae5da2-6cec-4d8b-8125-4fb963572c2f.png"
              alt="Vilaris AI Logo"
              className="object-contain"
              style={{ width: '90px', height: '90px' }}
            />
          </div>
          <div className="hidden md:flex items-center gap-10">
            {['Services', 'Industries', 'Case Studies', 'Contact'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase().replace(' ', '-')}`}
                className={`transition-colors text-sm font-medium tracking-wide cursor-pointer ${
                  isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-[#0D1B2A]'
                }`}
              >
                {item}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {/* Day / Night Toggle */}
            <button
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className={`relative w-14 h-7 rounded-full border transition-all duration-500 cursor-pointer flex items-center px-1 ${
                isDark
                  ? 'bg-[#14B8A6]/20 border-[#14B8A6]/40'
                  : 'bg-amber-50 border-amber-200'
              }`}
            >
              {/* Track icons */}
              <span className={`absolute left-1.5 text-xs transition-opacity duration-300 ${isDark ? 'opacity-40' : 'opacity-100'}`}>
                ☀️
              </span>
              <span className={`absolute right-1.5 text-xs transition-opacity duration-300 ${isDark ? 'opacity-100' : 'opacity-40'}`}>
                🌙
              </span>
              {/* Sliding knob */}
              <span
                className={`w-5 h-5 rounded-full shadow-md transition-all duration-500 z-10 ${
                  isDark
                    ? 'translate-x-7 bg-[#14B8A6]'
                    : 'translate-x-0 bg-amber-400'
                }`}
              />
            </button>

            <button className="bg-[#14B8A6] text-white px-7 py-2.5 rounded-full text-sm font-semibold hover:bg-[#0F9688] hover:shadow-lg hover:shadow-[#14B8A6]/30 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Book a Call
            </button>
          </div>
        </div>
      </nav>

      {/* Animated Gradient Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse"
          style={{ background: 'rgba(20,184,166,0.12)', animationDuration: '4s' }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full blur-3xl animate-pulse"
          style={{ background: 'rgba(20,184,166,0.08)', animationDuration: '6s', animationDelay: '1s' }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-3xl animate-pulse"
          style={{ background: 'rgba(15,150,136,0.06)', animationDuration: '8s', animationDelay: '2s' }}
        />
      </div>

      {/* Hero Content */}
      <div className="relative flex-1 flex flex-col items-center justify-center pt-36 pb-24 px-8">
        <div className="relative z-10 max-w-6xl mx-auto text-center w-full">

          {/* Eyebrow Label */}
          <div className={`inline-flex items-center gap-2 border border-[#14B8A6]/30 rounded-full px-5 py-2 mb-10 animate-fade-up backdrop-blur-sm ${
            isDark ? 'bg-white/5' : 'bg-[#14B8A6]/8'
          }`}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#14B8A6] animate-pulse inline-block" />
            <span className="text-[#14B8A6] text-xs font-semibold tracking-widest uppercase">
              AI-Driven Operational Systems
            </span>
          </div>

          <h1
            className={`text-6xl md:text-8xl font-bold mb-8 leading-[1.05] tracking-tight animate-fade-up transition-colors duration-500 ${
              isDark ? 'text-white' : 'text-[#0D1B2A]'
            }`}
            style={{ letterSpacing: '-0.02em', animationDelay: '0.1s' }}
          >
            Operational Intelligence,<br />
            <span className="text-[#14B8A6]">Perfectly Aligned</span>
          </h1>

          <p
            className={`text-xl md:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed font-light animate-fade-up transition-colors duration-500 ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}
            style={{ animationDelay: '0.2s' }}
          >
            Vilaris AI transforms fragmented workflows into coordinated intelligent systems.
            We help businesses automate operations, streamline decision making, and create infrastructure that scales.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-5 mb-24 animate-fade-up" style={{ animationDelay: '0.4s' }}>
            <button className="bg-[#14B8A6] text-white px-10 py-4 rounded-full text-base font-semibold hover:bg-[#0F9688] hover:shadow-2xl hover:shadow-[#14B8A6]/40 hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Book a Strategy Call
            </button>
            <button
              className={`border-2 px-10 py-4 rounded-full text-base font-semibold hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm ${
                isDark
                  ? 'border-white/20 text-white hover:bg-white/5 hover:border-white/40'
                  : 'border-[#0D1B2A]/20 text-[#0D1B2A] hover:bg-[#0D1B2A]/5 hover:border-[#0D1B2A]/40'
              }`}
            >
              See How It Works
            </button>
          </div>

          {/* Hero Visual - Enhanced Convergence Graphic */}
          <div className="relative w-full max-w-5xl mx-auto h-[420px] animate-fade-up" style={{ animationDelay: '0.6s' }}>
            <svg className="w-full h-full" viewBox="0 0 900 420" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="lineGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#14B8A6', stopOpacity: 0.9 }} />
                  <stop offset="100%" style={{ stopColor: '#14B8A6', stopOpacity: 0.1 }} />
                </linearGradient>
                <linearGradient id="lineGrad2" x1="100%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#14B8A6', stopOpacity: 0.9 }} />
                  <stop offset="100%" style={{ stopColor: '#14B8A6', stopOpacity: 0.1 }} />
                </linearGradient>
                <linearGradient id="coreGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#14B8A6', stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: '#0F9688', stopOpacity: 0.6 }} />
                </linearGradient>
                <radialGradient id="glowGrad" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" style={{ stopColor: '#14B8A6', stopOpacity: 0.4 }} />
                  <stop offset="100%" style={{ stopColor: '#14B8A6', stopOpacity: 0 }} />
                </radialGradient>
                <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <filter id="softGlow" x="-100%" y="-100%" width="300%" height="300%">
                  <feGaussianBlur stdDeviation="8" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              <circle cx="450" cy="210" r="140" fill="url(#glowGrad)" />

              <circle cx="450" cy="210" r="130" fill="none" stroke="#14B8A6" strokeWidth="0.5" opacity="0.15">
                <animate attributeName="r" from="130" to="200" dur="3.5s" repeatCount="indefinite" />
                <animate attributeName="opacity" from="0.15" to="0" dur="3.5s" repeatCount="indefinite" />
              </circle>
              <circle cx="450" cy="210" r="110" fill="none" stroke="#14B8A6" strokeWidth="0.5" opacity="0.2">
                <animate attributeName="r" from="110" to="170" dur="3.5s" repeatCount="indefinite" begin="0.7s" />
                <animate attributeName="opacity" from="0.2" to="0" dur="3.5s" repeatCount="indefinite" begin="0.7s" />
              </circle>

              <line x1="30" y1="80" x2="450" y2="210" stroke="url(#lineGrad1)" strokeWidth="1.5" filter="url(#glow)" opacity="0.7" />
              <line x1="30" y1="160" x2="450" y2="210" stroke="url(#lineGrad1)" strokeWidth="1.5" filter="url(#glow)" opacity="0.5" />
              <line x1="30" y1="240" x2="450" y2="210" stroke="url(#lineGrad1)" strokeWidth="1" filter="url(#glow)" opacity="0.4" />
              <line x1="30" y1="320" x2="450" y2="210" stroke="url(#lineGrad1)" strokeWidth="1.5" filter="url(#glow)" opacity="0.6" />
              <line x1="120" y1="30" x2="450" y2="210" stroke="url(#lineGrad1)" strokeWidth="1" filter="url(#glow)" opacity="0.35" />
              <line x1="120" y1="390" x2="450" y2="210" stroke="url(#lineGrad1)" strokeWidth="1" filter="url(#glow)" opacity="0.35" />

              <line x1="870" y1="80" x2="450" y2="210" stroke="url(#lineGrad2)" strokeWidth="1.5" filter="url(#glow)" opacity="0.7" />
              <line x1="870" y1="160" x2="450" y2="210" stroke="url(#lineGrad2)" strokeWidth="1.5" filter="url(#glow)" opacity="0.5" />
              <line x1="870" y1="240" x2="450" y2="210" stroke="url(#lineGrad2)" strokeWidth="1" filter="url(#glow)" opacity="0.4" />
              <line x1="870" y1="320" x2="450" y2="210" stroke="url(#lineGrad2)" strokeWidth="1.5" filter="url(#glow)" opacity="0.6" />
              <line x1="780" y1="30" x2="450" y2="210" stroke="url(#lineGrad2)" strokeWidth="1" filter="url(#glow)" opacity="0.35" />
              <line x1="780" y1="390" x2="450" y2="210" stroke="url(#lineGrad2)" strokeWidth="1" filter="url(#glow)" opacity="0.35" />

              {[80, 160, 240, 320].map((y, i) => (
                <g key={`left-${i}`}>
                  <rect x="14" y={y - 14} width="28" height="28" rx="6" fill={nodeRectFill} stroke="#14B8A6" strokeWidth="1.5" opacity="0.9" filter="url(#glow)" />
                  <circle cx="28" cy={y} r="4" fill="#14B8A6" opacity="0.9" filter="url(#glow)">
                    <animate attributeName="opacity" from="0.9" to="0.3" dur="2s" repeatCount="indefinite" begin={`${i * 0.4}s`} />
                  </circle>
                </g>
              ))}

              {[80, 160, 240, 320].map((y, i) => (
                <g key={`right-${i}`}>
                  <rect x="858" y={y - 14} width="28" height="28" rx="6" fill={nodeRectFill} stroke="#14B8A6" strokeWidth="1.5" opacity="0.9" filter="url(#glow)" />
                  <circle cx="872" cy={y} r="4" fill="#14B8A6" opacity="0.9" filter="url(#glow)">
                    <animate attributeName="opacity" from="0.9" to="0.3" dur="2s" repeatCount="indefinite" begin={`${i * 0.4 + 0.2}s`} />
                  </circle>
                </g>
              ))}

              <circle cx="450" cy="210" r="90" fill="none" stroke="#14B8A6" strokeWidth="1.5" opacity="0.25" strokeDasharray="4,8">
                <animateTransform attributeName="transform" type="rotate" from="0 450 210" to="360 450 210" dur="25s" repeatCount="indefinite" />
              </circle>
              <circle cx="450" cy="210" r="65" fill="none" stroke="#14B8A6" strokeWidth="1" opacity="0.35" strokeDasharray="2,6">
                <animateTransform attributeName="transform" type="rotate" from="360 450 210" to="0 450 210" dur="18s" repeatCount="indefinite" />
              </circle>

              <circle cx="540" cy="210" r="5" fill="#14B8A6" opacity="0.9" filter="url(#softGlow)">
                <animateTransform attributeName="transform" type="rotate" from="0 450 210" to="360 450 210" dur="8s" repeatCount="indefinite" />
              </circle>
              <circle cx="515" cy="210" r="3.5" fill="#14B8A6" opacity="0.7" filter="url(#glow)">
                <animateTransform attributeName="transform" type="rotate" from="180 450 210" to="540 450 210" dur="5s" repeatCount="indefinite" />
              </circle>

              <circle cx="450" cy="210" r="45" fill="url(#coreGrad)" opacity="0.12" />
              <circle cx="450" cy="210" r="30" fill="url(#coreGrad)" opacity="0.22" />
              <circle cx="450" cy="210" r="16" fill="#14B8A6" opacity="0.9" filter="url(#softGlow)">
                <animate attributeName="r" from="16" to="20" dur="2.5s" repeatCount="indefinite" />
                <animate attributeName="opacity" from="0.9" to="0.6" dur="2.5s" repeatCount="indefinite" />
              </circle>

              <line x1="200" y1="210" x2="330" y2="210" stroke="#14B8A6" strokeWidth="0.5" opacity="0.2" strokeDasharray="3,6" />
              <line x1="570" y1="210" x2="700" y2="210" stroke="#14B8A6" strokeWidth="0.5" opacity="0.2" strokeDasharray="3,6" />
            </svg>

            {/* Floating stat badges */}
            <div
              className={`absolute top-6 left-8 backdrop-blur-md border border-[#14B8A6]/20 rounded-xl px-4 py-3 animate-fade-up transition-colors duration-500 ${
                isDark ? 'bg-white/5' : 'bg-white border-gray-100'
              }`}
              style={{ animationDelay: '1s' }}
            >
              <p className="text-[#14B8A6] text-xl font-bold">+38%</p>
              <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Booked Jobs</p>
            </div>
            <div
              className={`absolute top-6 right-8 backdrop-blur-md border border-[#14B8A6]/20 rounded-xl px-4 py-3 animate-fade-up transition-colors duration-500 ${
                isDark ? 'bg-white/5' : 'bg-white border-gray-100'
              }`}
              style={{ animationDelay: '1.1s' }}
            >
              <p className="text-[#14B8A6] text-xl font-bold">-60%</p>
              <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Admin Workload</p>
            </div>
            <div
              className={`absolute bottom-6 left-1/2 -translate-x-1/2 backdrop-blur-md border border-[#14B8A6]/20 rounded-xl px-5 py-3 animate-fade-up transition-colors duration-500 ${
                isDark ? 'bg-white/5' : 'bg-white border-gray-100'
              }`}
              style={{ animationDelay: '1.2s' }}
            >
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-[#14B8A6] animate-pulse inline-block" />
                <p className={`text-xs font-medium tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Systems Active — 3 Industries Served
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up {
          animation: fade-up 1s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </section>
  );
}
